/**
 * Search state held in the URL.
 *
 * The query string is the single source of truth for filters, paging and sort,
 * so a search is shareable, survives a reload, and the browser's back button
 * steps through refinements. Components read and write it through this hook
 * rather than keeping a parallel copy in component state.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  EMPTY_FILTERS,
  JUDGMENT_STATUSES,
  PARTY_ROLES,
  SORT_FIELDS,
  type JudgmentFilters,
  type JudgmentStatus,
  type PartyRole,
  type SortDirection,
  type SortField,
} from '@/types/api'

const DEFAULT_PAGE_SIZE = 25

export interface SearchState {
  filters: JudgmentFilters
  page: number
  pageSize: number
  sortBy: SortField
  sortDir: SortDirection
}

function parseIntOr(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function parseTriState(value: string | null): boolean | null {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

function readState(params: URLSearchParams): SearchState {
  const sortBy = params.get('sortBy')
  const sortDir = params.get('sortDir')
  const partyRole = params.get('partyRole')

  return {
    filters: {
      q: params.get('q') ?? '',
      status: params
        .getAll('status')
        .filter((value): value is JudgmentStatus =>
          (JUDGMENT_STATUSES as readonly string[]).includes(value),
        ),
      filingLocation: params.getAll('filingLocation'),
      partyRole: (PARTY_ROLES as readonly string[]).includes(partyRole ?? '')
        ? (partyRole as PartyRole)
        : null,
      dateFrom: params.get('dateFrom') ?? '',
      dateTo: params.get('dateTo') ?? '',
      amountMin: params.get('amountMin') ?? '',
      amountMax: params.get('amountMax') ?? '',
      hasDetails: parseTriState(params.get('hasDetails')),
      captureId: params.has('captureId')
        ? parseIntOr(params.get('captureId'), 0) || null
        : null,
    },
    page: parseIntOr(params.get('page'), 1),
    pageSize: parseIntOr(params.get('pageSize'), DEFAULT_PAGE_SIZE),
    sortBy: (SORT_FIELDS as readonly string[]).includes(sortBy ?? '')
      ? (sortBy as SortField)
      : 'entered_on',
    sortDir: sortDir === 'asc' ? 'asc' : 'desc',
  }
}

function writeState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams()
  const { filters } = state

  if (filters.q.trim()) params.set('q', filters.q.trim())
  filters.status.forEach((value) => params.append('status', value))
  filters.filingLocation.forEach((value) => params.append('filingLocation', value))
  if (filters.partyRole) params.set('partyRole', filters.partyRole)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.amountMin) params.set('amountMin', filters.amountMin)
  if (filters.amountMax) params.set('amountMax', filters.amountMax)
  if (filters.hasDetails !== null) params.set('hasDetails', String(filters.hasDetails))
  if (filters.captureId !== null) params.set('captureId', String(filters.captureId))

  // Defaults stay out of the URL so a plain search has a clean address.
  if (state.page !== 1) params.set('page', String(state.page))
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(state.pageSize))
  if (state.sortBy !== 'entered_on') params.set('sortBy', state.sortBy)
  if (state.sortDir !== 'desc') params.set('sortDir', state.sortDir)

  return params
}

export function countActiveFilters(filters: JudgmentFilters): number {
  let total = 0
  if (filters.q.trim()) total += 1
  total += filters.status.length
  total += filters.filingLocation.length
  if (filters.partyRole) total += 1
  if (filters.dateFrom) total += 1
  if (filters.dateTo) total += 1
  if (filters.amountMin) total += 1
  if (filters.amountMax) total += 1
  if (filters.hasDetails !== null) total += 1
  if (filters.captureId !== null) total += 1
  return total
}

export function useSearchState() {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readState(params), [params])

  const commit = useCallback(
    (next: SearchState, options?: { replace?: boolean }) => {
      setParams(writeState(next), { replace: options?.replace ?? false })
    },
    [setParams],
  )

  /** Change filters. Always returns to page 1: the old offset is meaningless. */
  const setFilters = useCallback(
    (update: Partial<JudgmentFilters>) => {
      commit({ ...state, filters: { ...state.filters, ...update }, page: 1 })
    },
    [commit, state],
  )

  const setPage = useCallback(
    (page: number) => commit({ ...state, page }),
    [commit, state],
  )

  const setPageSize = useCallback(
    (pageSize: number) => commit({ ...state, pageSize, page: 1 }),
    [commit, state],
  )

  /** Clicking the active column flips direction; a new column starts desc. */
  const setSort = useCallback(
    (field: SortField) => {
      const sortDir: SortDirection =
        state.sortBy === field && state.sortDir === 'desc' ? 'asc' : 'desc'
      commit({ ...state, sortBy: field, sortDir, page: 1 })
    },
    [commit, state],
  )

  const resetFilters = useCallback(() => {
    commit({ ...state, filters: { ...EMPTY_FILTERS }, page: 1 })
  }, [commit, state])

  const toggleArrayFilter = useCallback(
    <K extends 'status' | 'filingLocation'>(
      key: K,
      value: JudgmentFilters[K][number],
    ) => {
      const current = state.filters[key] as string[]
      const next = current.includes(value as string)
        ? current.filter((entry) => entry !== value)
        : [...current, value as string]
      setFilters({ [key]: next } as Partial<JudgmentFilters>)
    },
    [state.filters, setFilters],
  )

  return {
    ...state,
    activeFilterCount: countActiveFilters(state.filters),
    setFilters,
    setPage,
    setPageSize,
    setSort,
    resetFilters,
    toggleArrayFilter,
  }
}
